import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { DEFAULT_CHAT_API_URL, DEFAULT_CHAT_MODEL } from "@/lib/llm-provider";

/**
 * Site assistant.
 *
 * The provider is called server-side only, so the API key never reaches the
 * browser. Any OpenAI-compatible endpoint works — Gemini by default, or
 * Groq / OpenRouter / anything else by changing the environment:
 *
 *   CHAT_API_KEY   required; without it the endpoint reports unconfigured
 *   CHAT_API_URL   default in llm-provider.ts (Google's OpenAI-compatible URL)
 *   CHAT_MODEL     default in llm-provider.ts (gemini-2.5-flash-lite)
 *
 * Until 30 Sep 2026 this ran on NVIDIA NIM's free tier with kimi-k3. On 29
 * Sep every call to it timed out — a one-word prompt got no answer in three
 * minutes — and the key could invoke nothing else in the catalogue, so for a
 * visitor the assistant had simply stopped working. The notes below are what
 * that search found, kept in case NVIDIA is ever tried again.
 *
 * `/v1/models` (public, unauthenticated) lists NVIDIA's full catalogue, but a
 * free-tier key can only INVOKE a small subset of it — most catalogued ids
 * 404 with "Function ... Not found for account" even though they're listed.
 * Verify a candidate with a real completions call, not just by checking it
 * appears in /v1/models. Three models were ruled out before this one:
 *
 *   - nvidia/llama-3.1-nemotron-70b-instruct: 404s for this account despite
 *     being listed.
 *   - openai/gpt-oss-20b: invokable and cleanly separates chain-of-thought
 *     into its own `reasoning` field, but took over two minutes to answer on
 *     this free tier — unusable for a live chat widget.
 *   - nvidia/nemotron-3-super-120b-a12b: fast (~2s), but inlines
 *     chain-of-thought straight into `content` with no delimiter, and
 *     whether it does so is apparently nondeterministic per call — one
 *     request at max_tokens 150 leaked "Hmm, the user is asking..." into the
 *     visible reply, another at 400 tokens did not. A model that sometimes
 *     shows its internal monologue to a visitor is not safe to use here.
 *
 * kimi-k3 answers only in `content` with no observed reasoning leakage, in
 * roughly 6-13s. The free tier also rate-limits aggressively (429s appeared
 * after a handful of test calls in quick succession), so expect real users
 * to occasionally see the "try again in a moment" reply below.
 *
 * If the provider is unconfigured, rate-limited or failing, the caller gets a
 * clear reply pointing at the pages rather than an error — a broken assistant
 * must never look like a broken site.
 */

const TIMEOUT_MS = 28000; // well past a normal reply; beyond this the fallback serves the visitor better
const MAX_REPLY_TOKENS = 500; // some models spend part of this on a hidden reasoning field

// Free tiers are metered, and the endpoint is public. Cap per-visitor usage so
// one browser cannot exhaust the quota for everyone.
const WINDOW_MS = 60_000;
const MAX_PER_WINDOW = 6;
const buckets = new Map<string, number[]>();

// The same bare-path pattern ChatWidget uses to render links, so whatever
// this treats as a link is exactly what the visitor's browser will too.
const PATH_PATTERN = /\/[a-z0-9#][a-z0-9\-/#]*/gi;

/**
 * Rewrites any path the model produced that is not on VALID_CHAT_PATHS.
 *
 * A model can misremember a URL it was given correctly — kimi-k3's very
 * first live reply linked to /guides/meta-ads-plus, a plausible-looking
 * neighbour of the real /guides/meta-advantage-plus. Every path in a reply
 * is checked here before the reply leaves the server, so a hallucinated
 * link becomes a working one rather than a dead one a visitor clicks.
 */
async function sanitizeLinks(text: string): Promise<string> {
  const { VALID_CHAT_PATHS } = await import("@/lib/chat-knowledge");
  const valid = new Set<string>(VALID_CHAT_PATHS);
  return text.replace(PATH_PATTERN, (path) => {
    if (valid.has(path)) return path;
    const fallback = path.startsWith("/guides") ? "/guides" : "/#audit";
    console.warn(`[chat] rewrote unrecognised path "${path}" -> "${fallback}"`);
    return fallback;
  });
}

function rateLimited(key: string): boolean {
  const now = Date.now();
  const hits = (buckets.get(key) ?? []).filter((t) => now - t < WINDOW_MS);
  hits.push(now);
  buckets.set(key, hits);
  // Keep the map from growing without bound on a long-lived process.
  if (buckets.size > 5000) {
    for (const [k, v] of buckets) {
      if (v.every((t) => now - t > WINDOW_MS)) buckets.delete(k);
    }
  }
  return hits.length > MAX_PER_WINDOW;
}

const messageSchema = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.string().trim().min(1).max(2000),
});

const requestSchema = z.object({
  message: z.string().trim().min(1).max(2000),
  // Short rolling history so follow-up questions make sense. Capped to keep
  // the prompt small and the free-tier token spend predictable.
  history: z.array(messageSchema).max(8).default([]),
});

const FALLBACK =
  "I can't reach the assistant right now. The guides at /guides cover campaign structure, the learning phase, creative testing and Advantage+, and you can request an account review at /#audit.";

export const askAssistant = createServerFn({ method: "POST" })
  .validator((data: unknown) => requestSchema.parse(data))
  .handler(async ({ data }) => {
    const apiKey = process.env["CHAT_API_KEY"];
    if (!apiKey) {
      console.warn("[chat] CHAT_API_KEY not set — assistant disabled");
      return { reply: FALLBACK, ok: false as const };
    }

    // Imported lazily so this module stays cheap to load. Only used to bucket
    // rate limiting; behind Cloudflare the real client IP arrives forwarded.
    let clientKey = "anon";
    try {
      const { getRequestIP } = await import("@tanstack/react-start/server");
      clientKey = getRequestIP({ xForwardedFor: true }) ?? "anon";
    } catch {
      // Not fatal — everyone shares one bucket rather than the call failing.
    }

    if (rateLimited(clientKey)) {
      return {
        reply: "That's a few questions in quick succession — give it a moment and try again.",
        ok: false as const,
      };
    }

    const { CHAT_SYSTEM_PROMPT } = await import("@/lib/chat-knowledge");

    try {
      const response = await fetch(process.env["CHAT_API_URL"] ?? DEFAULT_CHAT_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        signal: AbortSignal.timeout(TIMEOUT_MS),
        body: JSON.stringify({
          model: process.env["CHAT_MODEL"] ?? DEFAULT_CHAT_MODEL,
          messages: [
            { role: "system", content: CHAT_SYSTEM_PROMPT },
            ...data.history,
            { role: "user", content: data.message },
          ],
          temperature: 0.2,
          max_tokens: MAX_REPLY_TOKENS,
        }),
      });

      if (!response.ok) {
        console.error(`[chat] provider returned ${response.status}: ${await response.text()}`);
        return { reply: FALLBACK, ok: false as const };
      }

      const body = (await response.json()) as {
        choices?: Array<{ message?: { content?: string } }>;
      };
      const reply = body.choices?.[0]?.message?.content?.trim();
      if (!reply) {
        console.error("[chat] provider returned no content");
        return { reply: FALLBACK, ok: false as const };
      }

      // Some reasoning models emit a <think> block before the answer.
      const cleaned = reply.replace(/<think>[\s\S]*?<\/think>/gi, "").trim();
      const safe = await sanitizeLinks(cleaned);
      return { reply: safe || FALLBACK, ok: true as const };
    } catch (error) {
      console.error("[chat] request threw:", error);
      return { reply: FALLBACK, ok: false as const };
    }
  });
